import React, { useMemo, useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Box,
    Button,
    Collapse,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { ButtonVariant } from "Constant/button";
import { routes } from "Constant/routes";
import { Link, useNavigate, useParams } from "react-router-dom";

import StandartButton from "components/atoms/StandartButton";

import {
    AddFunnelSteps,
    AddLandingPageSteps,
    EditFunnelSteps,
    EditLandingPageSteps,
    STEPS,
} from "../../AddEditLandingPage.types";

const SIDEBAR_WIDTH = {
    compact: 72,
    expanded: 300,
};

const StepNavigationItem = ({
    compact = false,
    index,
    isActive,
    isCompleted,
    label,
    onClick,
    successColor,
}) => {
    const isAvailable = isCompleted || isActive;

    if (compact) {
        return (
            <Tooltip title={label} placement="right">
                <Box
                    component="button"
                    type="button"
                    onClick={onClick}
                    disabled={!isAvailable}
                    aria-label={`${index + 1}. ${label}`}
                    sx={{
                        width: 44,
                        height: 44,
                        p: 0,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: isActive ? "#000" : "#EDF0F6",
                        color: isActive
                            ? "#fff"
                            : isCompleted
                              ? successColor
                              : "#333",
                        border: isActive
                            ? "2px solid #000"
                            : isCompleted
                              ? `2px solid ${successColor}`
                              : "2px solid #D5DAE3",
                        cursor: isAvailable ? "pointer" : "not-allowed",
                        opacity: isAvailable ? 1 : 0.5,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": isAvailable
                            ? {
                                  transform: "scale(1.05)",
                                  backgroundColor: isActive
                                      ? "#000"
                                      : "#FFFFFF",
                              }
                            : {},
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {index + 1}
                    </Typography>
                </Box>
            </Tooltip>
        );
    }

    return (
        <Paper
            component="button"
            type="button"
            onClick={onClick}
            disabled={!isAvailable}
            sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                minWidth: 0,
                p: 2,
                borderRadius: 2,
                textAlign: "left",
                cursor: isAvailable ? "pointer" : "not-allowed",
                backgroundColor: "#EDF0F6",
                border: isActive ? "2px solid #212121" : "2px solid #EDF0F6",
                opacity: isAvailable ? 1 : 0.55,
                boxShadow: "none",
                transition: "all 0.2s ease-in-out",
                "&:hover": isAvailable
                    ? {
                          borderColor: isActive ? "#212121" : "#D5DAE3",
                          backgroundColor: "#FFFFFF",
                      }
                    : {},
            }}
        >
            <Box
                sx={{
                    mr: 1.5,
                    width: 24,
                    height: 24,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    border: "1px solid black",
                    backgroundColor: isActive ? "black" : "#EDF0F6",
                    color: isActive ? "#fff" : "black",
                }}
            >
                <Typography variant="body2">{index + 1}</Typography>
            </Box>
            <Typography
                variant="body1"
                sx={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontWeight: isActive ? "bold" : "normal",
                    color: "#333",
                }}
            >
                {label}
            </Typography>
            {isCompleted && (
                <CheckCircleIcon
                    sx={{ ml: "auto", flexShrink: 0, fontSize: 22 }}
                    color="success"
                />
            )}
        </Paper>
    );
};

export default function LandingPageSidebar({
    completedSteps,
    isDraft,
    isStepDirty,
    setIsStepDirty,
    step,
    setStep,
    selectedTemplate,
}) {
    const isEditMode = !!useParams()?.id;
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    const isWideDesktop = useMediaQuery(theme.breakpoints.up("lg"));
    const [isMobileExpanded, setIsMobileExpanded] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const Steps = useMemo(() => {
        const handlers = [
            {
                predicate: () => selectedTemplate === 7,
                handler: () => (isEditMode ? EditFunnelSteps : AddFunnelSteps),
            },
            {
                predicate: () => isEditMode,
                handler: () => EditLandingPageSteps,
            },
            {
                predicate: () => !isEditMode,
                handler: () => AddLandingPageSteps,
            },
        ];
        return handlers.find((handler) => handler.predicate())?.handler();
    }, [isEditMode, selectedTemplate]);

    const successColor = theme.palette.success.main;

    const filteredStepKeys = useMemo(() => {
        if (!Steps) return [];

        return Object.keys(Steps)?.filter((stepKey) =>
            !isDraft && isEditMode
                ? stepKey !== EditLandingPageSteps.PREVIEW_AND_LAUNCH
                : true,
        );
    }, [Steps, isDraft, isEditMode]);

    const handleStepSelection = (stepInfo) => {
        if (!completedSteps?.includes(stepInfo.key)) return;

        if (isStepDirty) {
            const confirmChange = window.confirm(
                "You have unsaved changes. Are you sure you want to switch steps?",
            );

            if (!confirmChange) return;

            setIsStepDirty(false);
        }

        setStep(stepInfo.key);
        setIsMobileExpanded(false);
    };

    const showExpandedDesktop = isWideDesktop && !isSidebarCollapsed;
    const renderStepItems = (compact = false) =>
        filteredStepKeys.map((stepKey, index) => {
            const stepInfo = STEPS[stepKey];

            return (
                <StepNavigationItem
                    key={stepKey}
                    compact={compact}
                    index={index}
                    isActive={stepKey === step}
                    isCompleted={completedSteps?.includes(stepInfo.key)}
                    label={stepInfo.label}
                    onClick={() => handleStepSelection(stepInfo)}
                    successColor={successColor}
                />
            );
        });

    if (!isDesktop) {
        const activeStep = STEPS[step];

        return (
            <Box
                component="nav"
                aria-label="Landing page wizard steps"
                sx={{
                    width: "100%",
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: "#F6F7F9",
                    borderBottom: "1px solid #E0E4EA",
                }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Link to={routes.home}>
                        <Box
                            component="img"
                            src="/assets/images/signal-sight.png"
                            alt="SignalSight Logo"
                            sx={{ display: "block", height: 34, width: "auto" }}
                        />
                    </Link>
                    <Button
                        type="button"
                        onClick={() =>
                            setIsMobileExpanded((isExpanded) => !isExpanded)
                        }
                        endIcon={
                            isMobileExpanded ? (
                                <ExpandLessIcon />
                            ) : (
                                <ExpandMoreIcon />
                            )
                        }
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            justifyContent: "space-between",
                            color: "#212121",
                            backgroundColor: "#FFFFFF",
                            border: "1px solid #D5DAE3",
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            "&:hover": { backgroundColor: "#FFFFFF" },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {activeStep?.label || "Steps"}
                        </Box>
                    </Button>
                    <Tooltip title="Exit wizard">
                        <IconButton
                            onClick={() => navigate("/landing-pages")}
                            aria-label="Exit wizard"
                        >
                            <ExitToAppIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
                <Collapse in={isMobileExpanded}>
                    <Stack spacing={1} sx={{ pt: 1.5 }}>
                        {renderStepItems()}
                    </Stack>
                </Collapse>
            </Box>
        );
    }

    return (
        <Stack
            component="nav"
            aria-label="Landing page wizard steps"
            sx={{
                flex: `0 0 ${
                    showExpandedDesktop
                        ? SIDEBAR_WIDTH.expanded
                        : SIDEBAR_WIDTH.compact
                }px`,
                width: showExpandedDesktop
                    ? SIDEBAR_WIDTH.expanded
                    : SIDEBAR_WIDTH.compact,
                minWidth: 0,
                alignSelf: "stretch",
                p: showExpandedDesktop ? 2 : 1.5,
                backgroundColor: "#F6F7F9",
                borderRight: "1px solid #E0E4EA",
                overflowY: "auto",
                overflowX: "hidden",
                transition: "width 0.2s ease, flex-basis 0.2s ease",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    minHeight: 42,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {showExpandedDesktop && (
                    <Link to={routes.home}>
                        <Box
                            component="img"
                            src="/assets/images/signal-sight.png"
                            alt="SignalSight Logo"
                            sx={{ display: "block", height: 42, width: "auto" }}
                        />
                    </Link>
                )}
                {isWideDesktop && (
                    <Tooltip
                        title={
                            isSidebarCollapsed
                                ? "Expand sidebar"
                                : "Collapse sidebar"
                        }
                        placement="right"
                    >
                        <IconButton
                            onClick={() =>
                                setIsSidebarCollapsed((collapsed) => !collapsed)
                            }
                            size="small"
                            aria-label={
                                isSidebarCollapsed
                                    ? "Expand sidebar"
                                    : "Collapse sidebar"
                            }
                            sx={{
                                position: showExpandedDesktop
                                    ? "absolute"
                                    : "static",
                                right: 0,
                                backgroundColor: "#FFFFFF",
                                border: "1px solid #D5DAE3",
                            }}
                        >
                            {isSidebarCollapsed ? (
                                <ChevronRightIcon />
                            ) : (
                                <ChevronLeftIcon />
                            )}
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {showExpandedDesktop ? (
                <>
                    <Box sx={{ pt: 4, pb: 3 }}>
                        <Typography variant="body2">
                            {isEditMode ? "Edit" : "Create"} your
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{ mt: 0.25, fontWeight: 600 }}
                        >
                            Landing Page
                        </Typography>
                    </Box>
                    <Stack spacing={1.25} sx={{ flexGrow: 1 }}>
                        {renderStepItems()}
                    </Stack>
                    <StandartButton
                        variant={ButtonVariant.BLACK_OUTLINED}
                        onClick={() => navigate("/landing-pages")}
                        style={{ width: "100%", height: "34px" }}
                        sx={{ mt: 3 }}
                    >
                        EXIT WIZARD
                    </StandartButton>
                </>
            ) : (
                <Stack
                    spacing={1.25}
                    alignItems="center"
                    sx={{ flexGrow: 1, pt: 2 }}
                >
                    {renderStepItems(true)}
                    <Box sx={{ flexGrow: 1 }} />
                    <Tooltip title="Exit wizard" placement="right">
                        <IconButton
                            onClick={() => navigate("/landing-pages")}
                            aria-label="Exit wizard"
                            sx={{
                                border: "1px solid #D5DAE3",
                                backgroundColor: "#FFFFFF",
                            }}
                        >
                            <ExitToAppIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            )}
        </Stack>
    );
}
